import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import webSocketService from '../services/websocketService.js'; // Importer le service centralisé

// Créer un contexte pour notre service WebSocket
export const SocketContext = createContext(null);

// Hook personnalisé pour accéder facilement au service et à son état
export const useSocket = () => useContext(SocketContext);

/**
 * Ce middleware est le pont entre l'état d'authentification de React et le service WebSocket.
 * Son seul rôle est de dire au service quand se connecter (utilisateur logué) ou se déconnecter.
 * Il écoute aussi les événements du service pour afficher des notifications globales.
 */
const WebSocketMiddleware = ({ children }) => {
  const { isAuthenticated, authToken } = useAuth();
  const [isConnected, setIsConnected] = useState(webSocketService.isConnected());

  useEffect(() => {
    // --- Définition des gestionnaires d'événements ---

    // Gère les changements d'état de la connexion (pour mettre à jour l'UI si besoin)
    const handleConnectionChange = ({ status }) => {
      console.log(`[WebSocketMiddleware] Statut de connexion changé: ${status}`);
      setIsConnected(status === 'connected');
    };

    // Gère la réception de n'importe quelle notification et affiche un toast
    const handleNewNotification = (notification) => {
      console.log('[WebSocketMiddleware] Notification reçue:', notification);
      if (notification && notification.message) {
        toast.info(notification.message, {
          toastId: notification.id || `notification-${Date.now()}`,
        });
        // Ici, on pourrait aussi appeler un contexte de notifications pour les stocker dans une liste
      }
    };

    // Gère les erreurs venant du service WebSocket
    const handleError = (error) => {
      console.error('[WebSocketMiddleware] Erreur WebSocket reçue:', error);
      toast.error(error.message || 'Erreur de connexion en temps réel.', { toastId: 'socket-error' });
    };

    // --- Abonnement aux événements du service ---
    const unsubscribeConnection = webSocketService.registerHandler('connection', handleConnectionChange);
    const unsubscribeNotification = webSocketService.registerHandler('notification', handleNewNotification); // Événement générique 'notification'
    const unsubscribeError = webSocketService.registerHandler('error', handleError);

    // --- Logique de connexion/déconnexion ---
    if (isAuthenticated && authToken) {
      // Si l'utilisateur est authentifié, on initialise/connecte le service avec le token
      console.log('[WebSocketMiddleware] Utilisateur authentifié. Connexion au WebSocket...');
      webSocketService.connect(authToken);
    } else {
      // Sinon, on s'assure que le service est bien déconnecté
      console.log('[WebSocketMiddleware] Utilisateur non authentifié. Déconnexion du WebSocket...');
      webSocketService.disconnect();
    }

    // --- Nettoyage ---
    // Cette fonction sera appelée lorsque le composant est démonté
    return () => {
      console.log('[WebSocketMiddleware] Nettoyage des abonnements WebSocket.');
      unsubscribeConnection();
      unsubscribeNotification();
      unsubscribeError();
    };
  }, [isAuthenticated, authToken]);

  // Fournir l'instance du service et son état de connexion aux composants enfants
  const contextValue = useMemo(() => ({
    socketService: webSocketService,
    isConnected,
  }), [isConnected]);

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export default WebSocketMiddleware;
